/**
 * [3.5 #4] 正文里的商品链接必须可点 —— 前端这一半。
 *
 * 需求两句话：
 *   ① 生成时把 `{product_url}` 换成真实商品 URL 并带 UTM，**渲染为可点链接**；
 *   ② **占位符没被替换就视为生成失败，不允许进入发布** —— 宁可重试也不能把带
 *      `{product_url}` 字样的内容发出去。
 *
 * 替换与 UTM 是 Agent / 后端那一半，这里**一个字都不补**：UTM 的参数命名、以及
 * campaign 取「任务 ID」的哪个 id 都还没定，前端自己拼一遍只会和服务端打的标重复，
 * 把归因劈成两份（同 attribution.ts 的立场：只说真实成立的那句）。
 *
 * 所以这个文件只做两件事。
 *
 * **可点。** 卡片正文一律先过 htmlToText，而它把 markdown 链接 `[label](url)` 压成
 * label、把 `<a>` 压成文字 —— 链接的**地址在预览里直接消失**了；裸 URL 则原样当普通
 * 文字画出去。「不可点」不只是没做成 `<a>`，是地址被丢掉了。`bodySegments` 先把链接
 * 从原始正文里摘出来存好、原地留个私用区标记，再交给 htmlToText 做纯文本化，最后按
 * 标记把 `<a>` 拼回去，顺带把剩下的裸 URL 也认成链接。
 *
 * **拦住。** `unresolvedProductPlaceholders` 是这条需求唯一的判定口径：发布链路两处
 * （`publishToChannels` 的预检、`publishNowJob` 的逐渠道循环）和卡片上的禁用态都读它，
 * 谁都不要再各写一个正则。
 */
import { htmlToText } from './htmlText';

/**
 * 未被替换的商品链接占位符。
 *
 * 宽一点是故意的：模型写出来的不止 `{product_url}` 一种 —— `{{product_url}}`、
 * `{product_link}`、多商品时的 `{product_url_2}` 都见过，它们发出去一样是一串大括号。
 * 反过来不做的是 URL 编码形态（`%7Bproduct_url%7D`）：那说明占位符已经被拼进了某个
 * 地址里，属于后端替换逻辑的 bug，不该由前端在这里悄悄兜住。
 */
const PLACEHOLDER_RE = /\{\{?\s*product[_\-\s]?(?:url|link)[a-z0-9_-]*\s*\}\}?/gi;

/** 正文里所有未被替换的占位符原文（去重、保留出现顺序）。空数组 = 干净。 */
export function unresolvedProductPlaceholders(body: string | null | undefined): string[] {
  const text = String(body ?? '');
  if (!text) return [];
  const seen = new Set<string>();
  for (const m of text.matchAll(PLACEHOLDER_RE)) seen.add(m[0].trim());
  return Array.from(seen);
}

export function hasUnresolvedProductPlaceholder(body: string | null | undefined): boolean {
  return unresolvedProductPlaceholders(body).length > 0;
}

/**
 * 拦下来时说给商家看的那句话。**一处措辞**，卡片、发布预检、日历直发都用它 ——
 * 三处各写一句的话，同一件事会有三种说法。
 *
 * 刻意说清三件事：发生了什么（占位符没被替换）、后果（一个字都没发出去）、
 * 下一步（让 Maya 重生成这篇）。不写「稍后重试」：同一份正文重试一万次也一样。
 */
export function placeholderBlockMessage(placeholders: string[]): string {
  const shown = placeholders.slice(0, 3).join(', ');
  return `The body still has ${shown} in it — the real product link was never substituted, so nothing was published. Ask Maya to regenerate this piece.`;
}

/** 正文被切成的可渲染片段：纯文字，或一个可点链接。 */
export type BodySegment =
  | { kind: 'text'; text: string }
  | { kind: 'link'; text: string; href: string }
  | { kind: 'image'; src: string; alt: string };

/* 私用区码点当标记：它不可能出现在正文里，也不会被 htmlToText 的行首 marker 剥离、
   空白折叠、DOMParser textContent 任何一步吃掉。 */
const MARK_OPEN = '\uE000';
const MARK_CLOSE = '\uE001';
const MARK_RE = new RegExp(`${MARK_OPEN}(\\d+)${MARK_CLOSE}`, 'g');

/** 只认绝对 http(s) 地址。相对地址（`/products/x`）在店铺前台是有效链接，但在
 *  聊天页点下去会把 SPA 导到一个 404，所以预览里留作纯文字。 */
const isAbsolute = (href: string) => /^https?:\/\//i.test(href.trim());

const stripTags = (s: string) => s.replace(/<[^>]+>/g, '');

/** 裸 URL。收尾的中英标点不属于地址（句末的 `。` `,` 和成对的 `)` 会被贪婪吃进去）。 */
const BARE_URL_RE = /https?:\/\/[^\s<>"'）】]+/g;
const TRAILING_PUNCT_RE = /[.,;:!?、。，；：！？)\]}'"]+$/;


/** 把正文里的链接摘出来，原地留标记，交给 htmlToText 之后再拼回去。 */
type CapturedNode =
  | { kind: 'link'; text: string; href: string }
  | { kind: 'image'; src: string; alt: string };

const attrOf = (tag: string, name: string): string => {
  const m = tag.match(new RegExp(`\\b${name}\\s*=\\s*["']([^"']*)["']`, 'i'));
  return m ? m[1] : '';
};

function captureLinks(body: string): { marked: string; nodes: CapturedNode[] } {
  const nodes: CapturedNode[] = [];
  const put = (text: string, href: string) =>
    `${MARK_OPEN}${nodes.push({ kind: 'link', text: text || href, href }) - 1}${MARK_CLOSE}`;
  const putImg = (src: string, alt: string) =>
    `${MARK_OPEN}${nodes.push({ kind: 'image', src, alt }) - 1}${MARK_CLOSE}`;

  // Inline <img> pulled out first (absolute src only): htmlToText would otherwise strip the
  // tag and its address, so a blog's inline pictures vanish from the preview. Must precede the
  // bare-URL pass, whose regex would otherwise swallow the http address inside src=.
  let marked = body.replace(/<img\b[^>]*>/gi, tag => {
    const src = attrOf(tag, 'src').trim();
    return isAbsolute(src) ? putImg(src, attrOf(tag, 'alt').trim()) : tag;
  });

  marked = marked.replace(
    /<a\b[^>]*\bhref\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi,
    (whole, href: string, inner: string) =>
      isAbsolute(href) ? put(stripTags(inner).trim(), href.trim()) : whole
  );
  // markdown 链接。前面那个字符要一起吃进来再还回去，为的是把 `![alt](url)`（图片，
  // 由卡头的主图承载）排除在外 —— 不用后行断言，老 Safari 上它会整段解析失败。
  marked = marked.replace(
    /(^|[^!])\[([^\]]*)\]\(\s*<?([^\s)>]+)>?[^)]*\)/g,
    (whole, pre: string, label: string, href: string) =>
      isAbsolute(href) ? pre + put(label.trim(), href.trim()) : whole
  );
  /* 裸 URL 也要在**进 htmlToText 之前**摘走，不能等纯文本化之后再认。
     htmlToText 最后一步是 `.replace(/[*_`~]+/g, '')`（剥 markdown 强调记号）——
     它会把地址里的下划线一起吃掉，`?utm_source=facebook` 出来变成 `?utmsource=facebook`。
     那是一条**今天就在生效**的显示 bug（预览里的带 UTM 地址一直是残的）；等做成
     可点链接之后就不只是难看了，href 是错的，点过去 404、UTM 也丢了。 */
  marked = marked.replace(BARE_URL_RE, raw => {
    const url = raw.replace(TRAILING_PUNCT_RE, '');
    return url ? put(url, url) + raw.slice(url.length) : raw;
  });
  return { marked, nodes };
}

/**
 * 正文 → 可渲染片段。`opts` 直接透给 htmlToText（`preserveBreaks` 决定是展开态的
 * 分段全文还是折叠态的一行摘要）。
 */
export function bodySegments(
  body: string | null | undefined,
  opts?: { preserveBreaks?: boolean }
): BodySegment[] {
  const raw = String(body ?? '');
  if (!raw) return [];
  const { marked, nodes } = captureLinks(raw);
  const plain = htmlToText(marked, opts);
  const out: BodySegment[] = [];
  const push = (text: string) => {
    if (text) out.push({ kind: 'text', text });
  };
  let last = 0;
  MARK_RE.lastIndex = 0;
  for (let m = MARK_RE.exec(plain); m; m = MARK_RE.exec(plain)) {
    push(plain.slice(last, m.index));
    const node = nodes[Number(m[1])];
    if (node?.kind === 'link') out.push({ kind: 'link', text: node.text, href: node.href });
    else if (node?.kind === 'image') out.push({ kind: 'image', src: node.src, alt: node.alt });
    last = m.index + m[0].length;
  }
  push(plain.slice(last));
  return out;
}

const segmentText = (s: BodySegment) => (s.kind === 'image' ? '' : s.text);

/** 片段列表按空行切成段落（卡片展开态逐段渲染，同原来 `split(/\n{2,}/)` 的口径）。 */
export function paragraphsOf(segments: BodySegment[]): BodySegment[][] {
  const paragraphs: BodySegment[][] = [];
  let cur: BodySegment[] = [];
  for (const seg of segments) {
    if (seg.kind !== 'text') {
      cur.push(seg);
      continue;
    }
    const parts = seg.text.split(/\n{2,}/);
    parts.forEach((part, i) => {
      if (i > 0) {
        paragraphs.push(cur);
        cur = [];
      }
      if (part) cur.push({ kind: 'text', text: part });
    });
  }
  paragraphs.push(cur);
  return paragraphs
    .map(trimEdges)
    .filter(p => p.some(s => s.kind !== 'text' || s.text.trim() !== ''));
}

/** 段落首尾的空白（原来那一步 `.map(t => t.trim())`）—— 只动首尾两个纯文字片段。 */
function trimEdges(paragraph: BodySegment[]): BodySegment[] {
  const out = paragraph.slice();
  const first = out[0];
  if (first && first.kind === 'text') out[0] = { kind: 'text', text: first.text.replace(/^\s+/, '') };
  const lastIdx = out.length - 1;
  const last = out[lastIdx];
  if (last && last.kind === 'text')
    out[lastIdx] = { kind: 'text', text: last.text.replace(/\s+$/, '') };
  return out.filter(s => s.kind !== 'text' || s.text !== '');
}

/** 片段列表还原成纯文本（折叠态量高度、以及归因判定都还按纯文本走）。 */
export function segmentsToText(segments: BodySegment[]): string {
  return segments.map(segmentText).join('');
}
